import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { primaryColor } from "../../../../../utils/comon";
import apiServices from "../../../../../services/apiService";
import encryptedDataService from "../../../../../services/encryptedDataService";

const VisitorPass = () => {
    const [searchParams] = useSearchParams();

    const [passDetails, setPassDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const rawSessionId = searchParams.get("session");
    const rawCardNumber = searchParams.get("m");
    
    // Restore any '+' characters that URL parsing converted to spaces
    const sessionId = rawSessionId ? rawSessionId.replace(/ /g, '+') : null;
    const cardNumber = rawCardNumber ? rawCardNumber.replace(/ /g, '+') : null;

    useEffect(() => {
        if (sessionId) {
            fetchPassDetails(sessionId, true);
        } else if (cardNumber) {
            fetchPassDetails(cardNumber, false);
        } else {
            setError("No visitor session or card number found in URL.");
        }
    }, [sessionId, cardNumber]);

    const fetchPassDetails = async (id, isSession) => {
        setLoading(true);
        try {
            const url = isSession ? `/Visitor/getPassBySession` : `/Visitor/getVisitorByCardNo`;
            const params = isSession ? { session: id } : { cardNo: id };

            const response = await apiServices({
                method: "GET",
                url: url,
                params: params
            });

            if (response?.success || response?.isSuccess) {
                const dataArray = response?.data?.data || response?.data || [];
                const firstData = Array.isArray(dataArray) ? dataArray[0] : dataArray;

                if (firstData) {
                    const units = Array.isArray(dataArray) ? dataArray : [firstData];
                    const parkingRow = units.find(u => u.slotName && u.parkingStatus !== "Completed");
                    const mappedData = {
                        parking: parkingRow ? {
                            slot: parkingRow.slotName,
                            status: parkingRow.parkingStatus,
                            vehicleNumber: parkingRow.vehicleNumber,
                            vehicleType: parkingRow.vehicleType
                        } : null,
                        full_name: firstData.visitorName || "",
                        coming_from: firstData.location || firstData.address || "Walk-in",
                        mobile: firstData.visitorMobileNumber || "",
                        qr_payload: firstData.qrNumber1 || `visitor-${firstData.visitorId}`,
                        valid_from: firstData.createdDate || new Date().toISOString(),
                        joiners: firstData.additionalCount || 0,
                        purpose_text: firstData.purposeOfVisit || "",
                        visitorType: firstData.visitorType || "",
                        units: Array.isArray(dataArray) ? dataArray : [firstData]
                    };
                    setPassDetails(mappedData);
                } else {
                    setError("Visitor not found.");
                }
            } else {
                setError(response?.message || "Failed to load pass details.");
            }
        } catch (err) {
            console.error("Error fetching pass details:", err);
            setError("An error occurred while loading the pass.");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return "--";
        return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };

    const formatTime = (iso) => {
        if (!iso) return "--";
        return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    };

    if (loading) {
        return <div className="flex-1 flex items-center justify-center p-4">Loading pass details...</div>;
    }

    const badgeStatus = (unit) => unit?.statusName || unit?.status;

    const isOpenUnit = (unit) => {
        const status = String(badgeStatus(unit) || "").toLowerCase();
        return status === "1" || status === "2" || status.includes("approve") || status.includes("pending");
    };

    const isPassExpired = passDetails?.units?.length > 0 && !passDetails.units.some(isOpenUnit);

const getStatusColor = (status) => {
    if (!status) return 'bg-gray-500';

    const s = status.toString().toLowerCase();

    // Approved / Checked In
    if (
        s === 'approved' ||
        s === '1' ||
        s === 'checkedin' ||
        s === 'checked in' ||
        s === '7'
    ) {
        return 'bg-[#10B981]';
    }

    // Rejected / CheckOut / Cancel / Block
    if (
        s === 'rejected' ||
        s === '3' ||
        s === 'checkout' ||
        s === 'check out' ||
        s === '4' ||
        s === 'cancel' ||
        s === '5' ||
        s === 'block' ||
        s === 'blocked' ||
        s === '6'
    ) {
        return 'bg-red-500';
    }

    // Pending
    if (s === 'pending' || s === '2') {
        return 'bg-yellow-500';
    }

    return 'bg-gray-500';
};

const getStatusTextColor = (status) => {
    if (!status) return 'text-gray-500';

    const s = status.toString().toLowerCase();

    // Approved / Checked In
    if (
        s === 'approved' ||
        s === '1' ||
        s === 'checkedin' ||
        s === 'checked in' ||
        s === '7'
    ) {
        return 'text-[#10B981]';
    }

    // Rejected / CheckOut / Cancel / Block
    if (
        s === 'rejected' ||
        s === '3' ||
        s === 'checkout' ||
        s === 'check out' ||
        s === '4' ||
        s === 'cancel' ||
        s === '5' ||
        s === 'block' ||
        s === 'blocked' ||
        s === '6'
    ) {
        return 'text-red-500';
    }

    // Pending
    if (s === 'pending' || s === '2') {
        return 'text-yellow-500';
    }

    return 'text-gray-500';
};

const getStatusLabel = (status) => {
    if (!status) return 'Unknown';

    const s = status.toString().toLowerCase();

    switch (s) {
        case '1':
        case 'approved':
            return 'Approved';

        case '2':
        case 'pending':
            return 'Pending';

        case '3':
        case 'rejected':
            return 'Rejected';

        case '4':
        case 'checkout':
        case 'check out':
            return 'CheckOut';

        case '5':
        case 'cancel':
            return 'Cancel';

        case '6':
        case 'block':
        case 'blocked':
            return 'Block';

        case '7':
        case 'checkedin':
        case 'checked in':
            return 'CheckedIn';

        default:
            return status;
    }
};

    return (
        <div className="flex-1 flex flex-col items-center justify-start bg-gray-300 p-4 overflow-y-auto scrollbar-hide w-full h-full min-h-[90vh]">
            <div className="w-full max-w-[360px] flex flex-col relative mt-4 mb-8">
                <div className="bg-[#0A0A0B] rounded-t-[1.5rem] p-6 relative overflow-hidden">
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-2xl font-black tracking-tight cursor-pointer"
                            onClick={() => window.open("https://myhello.in/", "_blank")}
                            style={{ color: primaryColor() }}>
                            Hello!
                        </h1>
                        <span className="text-[8px] font-bold text-white border border-white/40 px-3 py-1 rounded-full uppercase tracking-widest">
                            {passDetails?.visitorType} Visitor
                        </span>
                    </div>

                    <h2 className="text-white text-xl font-bold leading-none tracking-tight">
                        {passDetails?.full_name || "--"}
                    </h2>

                    <div className="flex items-center gap-3 mt-4 text-white text-xs font-bold">
                        <span>{passDetails?.coming_from || "--"}</span>
                        <span className="opacity-40">|</span>
                        <span>{passDetails?.mobile || "--"}</span>
                    </div>
                </div>

                {/* NOTCH DIVIDER */}
                <div className="relative bg-[#F8FAFC]">
                    {/* Left Notch */}
                    <div className="absolute left-[-12px] top-[-12px] w-6 h-6 bg-gray-300 rounded-full z-10" />
                    {/* Right Notch */}
                    <div className="absolute right-[-12px] top-[-12px] w-6 h-6 bg-gray-300 rounded-full z-10" />
                    {/* Dashed Line */}
                    <div className="absolute top-0 left-8 right-8 border-t-2 border-dashed border-gray-200" />
                </div>

                {/* MIDDLE SECTION (Off-white) */}
                <div className="bg-[#F8FAFC] px-6 pb-6 flex flex-col items-center">
                    <p className="text-[11px] font-bold text-[#75777F] tracking-[0.5px] p-2 mt-2">
                        Scan to Verify Entry
                    </p>

                    {/* QR Container */}
                    <div className="bg-white p-6 rounded-3xl relative shadow-sm mt-2">
                        {/* Corner Markers */}
                        <div className="absolute top-4 left-4 w-6 h-6 border-t-[3px] border-l-[3px] rounded-tl-md" style={{ borderColor: "#D4AF37" }} />
                        <div className="absolute top-4 right-4 w-6 h-6 border-t-[3px] border-r-[3px] rounded-tr-md" style={{ borderColor: "#D4AF37" }} />
                        <div className="absolute bottom-4 left-4 w-6 h-6 border-b-[3px] border-l-[3px] rounded-bl-md" style={{ borderColor: "#D4AF37" }} />
                        <div className="absolute bottom-4 right-4 w-6 h-6 border-b-[3px] border-r-[3px] rounded-br-md" style={{ borderColor: "#D4AF37" }} />

                        <div className="relative">
                            <QRCodeSVG
                                value={passDetails?.qr_payload || "visitor-pass"}
                                size={200}
                                bgColor="#ffffff"
                                fgColor="#000000"
                                level="M"
                                includeMargin={false}
                                className={isPassExpired ? "opacity-20 blur-sm transition-all" : ""}
                            />
                            {isPassExpired && (
                                <div className="absolute inset-0 flex items-center justify-center z-20">
                                    <div className="bg-red-600/90 text-white font-black text-xl px-4 py-2 rounded-lg transform -rotate-12 border-2 border-red-400 shadow-lg tracking-wider">
                                        EXPIRED
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Details Grid */}
                    <div className="w-full mt-6 grid grid-cols-3 gap-y-4">
                        <div className="border-r border-gray-100 pr-2">
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Date</p>
                            <p className="text-xs font-bold text-[#1E293B] mt-1">
                                {formatDate(passDetails?.valid_from)}
                            </p>
                        </div>
                        <div className="border-r border-gray-100 px-2 text-center">
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Time</p>
                            <p className="text-[13px] font-bold text-[#1E293B] mt-1">
                                {formatTime(passDetails?.valid_from)}
                            </p>
                        </div>
                        <div className="pl-2 text-right">
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Joiners</p>
                            <p className="text-[13px] font-bold text-[#1E293B] mt-1">
                                {passDetails?.joiners || 0}
                            </p>
                        </div>

                        <div className="col-span-2">
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Purpose of Visit</p>
                            <p className="text-[13px] font-bold text-[#1E293B] mt-1">
                                {passDetails?.purpose_text || "--"}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Coming From</p>
                            <p className="text-[13px] font-bold text-[#1E293B] mt-1">
                                {passDetails?.coming_from || "--"}
                            </p>
                        </div>
                    </div>

                    {passDetails?.parking && (
                        <div className="w-full mt-6 rounded-2xl border border-gray-200 bg-white p-4 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase">Parking Slot</p>
                                <p className="text-lg font-black text-[#1E293B] mt-1">{passDetails.parking.slot}</p>
                                {(passDetails.parking.vehicleNumber || passDetails.parking.vehicleType) && (
                                    <p className="text-[11px] font-bold text-gray-500 mt-1">
                                        {[passDetails.parking.vehicleNumber, passDetails.parking.vehicleType].filter(Boolean).join(" | ")}
                                    </p>
                                )}
                            </div>
                            <span className={`text-[10px] font-black px-3 py-1 rounded-full ${passDetails.parking.status === "Held" ? "bg-yellow-100 text-yellow-700" : "bg-emerald-100 text-emerald-700"}`}>
                                {passDetails.parking.status === "Held" ? "HELD - PENDING APPROVAL" : "ALLOTTED"}
                            </span>
                        </div>
                    )}

                    {/* VISITING CARD (Black) - Multi Unit Support */}
                    <div className="w-full mt-6 space-y-3">
                        {passDetails?.units?.map((unit, index) => (
                            <div key={index} className="w-full bg-[#0A0A0B] rounded-3xl p-5 relative overflow-hidden">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Visiting</p>
                                        <h3 className="text-white text-xs font-bold mt-2 leading-tight">
                                            {unit.hostName || "--"}, {unit.unitName || unit.unitId || "--"}
                                        </h3>
                                        <p className="text-gray-400 text-xs mt-1">
                                            {unit.siteName || "--"}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5 bg-[#0D1B16] border border-[#143228] px-3 py-1.5 rounded-full">
                                        <div className={`w-2 h-2 rounded-full ${getStatusColor(badgeStatus(unit))} animate-pulse`} />
                                        <span className={`text-[10px] font-black ${getStatusTextColor(badgeStatus(unit))} tracking-widest`}>{getStatusLabel(badgeStatus(unit))}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* BOTTOM SECTION (Off-white) */}
                <div className="bg-[#F8FAFC] rounded-b-[2.5rem] pb-6 cursor-pointer"
                    onClick={() => window.open("https://zentechinfo.com/", "_blank")}
                >
                    <div className="flex justify-center items-center mt-2">
                        <img
                            src="/zentech.svg"
                            alt="Zentech"
                            className="h-8 opacity-100"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                            }}
                        />
                        <span style={{ display: 'none' }} className="font-bold text-gray-500">Zentech Info Solutions</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VisitorPass;


